import { decryptData, encryptData } from "@/features/cryptography";
import supabase from "@/features/supabase";
import { store } from "@/store";
import { supabaseApi } from "@/store/services/supabase";
import { PortfolioExportProgressTransformStream } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportProgressTransformStream";
import { PortfolioExportResultsTransformStream } from "@/tools/aggregate/portfolio-export/classes/PortfolioExportResultsTransformStream";
import { PortfolioExportJobSchema } from "@/tools/aggregate/portfolio-export/types";
import {
  enqueuePortfolioAdaptationSummary,
  enqueuePortfolioSummary,
  streamProgress,
  streamResultsErrorsOnly,
} from "@/tools/aggregate/portfolio-export/utils/stream";
import { enqueueLargeSiteSummary } from "@/tools/risk-bands/large-site-screen/utils/stream";
import { autoAnalysedResilienceTypeValues } from "@/types/adaptation";
import { KeyPairAndSalt, SecretSalt } from "@/types/cryptography";
import { ProcessStatus } from "@/types/processes";
import { hexToArrayBuffer } from "@/utils/crypto";
import { delay } from "@/utils/promises";
import { cloneDeep } from "lodash/fp";

type DecryptedIdMaps = { supabaseId: string; decryptedId: string }[];

const PROGRESS_ABORTED_BY_USER = "PROGRESS_ABORTED_BY_USER";

export class PortfolioExportProgressTracker {
  public schemas: PortfolioExportJobSchema[] = [];

  constructor(
    private updateSchema: (schema: PortfolioExportJobSchema) => void,
    private keys: {
      user: KeyPairAndSalt;
      org: KeyPairAndSalt | null;
    },
    private inProcessingSchemas: { id: string; controller: AbortController }[] = []
  ) {}

  /**
   * Applies an update to the latest state of a schema rather than the snapshot captured when
   * tracking started, so values refreshed while streaming (e.g. autoAdaptationStatus) aren't reverted.
   */
  private updateCurrentSchema = (
    schema: PortfolioExportJobSchema,
    update: (current: PortfolioExportJobSchema) => Partial<PortfolioExportJobSchema>
  ) => {
    const current = cloneDeep(this.schemas.find((s) => s.id === schema.id) ?? schema);
    this.updateSchema({ ...current, ...update(current) });
  };

  /**
   * Decrypts encrypted IDs for Portfolio Export assets and resilience pathway actions.
   */
  private getDecryptedAnalysisIds = async (
    ids: { id: string; encrypted_id: string }[],
    schema: PortfolioExportJobSchema
  ): Promise<DecryptedIdMaps> => {
    const promises = ids.map(async (id) => ({
      supabaseId: id.id,
      decryptedId: await decryptData(schema.secret, hexToArrayBuffer(id.encrypted_id)),
    }));
    return await Promise.all(promises);
  };

  /**
   * Tracks progress for resilience pathway actions until processing is complete parallely
   */
  private trackAdaptationPathwayActionsProgress = async ({
    schema,
    controller,
    idx,
    completedAssets,
  }: {
    schema: PortfolioExportJobSchema;
    controller: AbortController;
    idx: number;
    completedAssets: PortfolioExportJobSchema["assets"];
  }) => {
    const { data: adaptationRows } = await supabase
      .from("adaptations")
      .select("id,adaptation_type")
      .eq("portfolio_id", schema.id)
      .in("adaptation_type", autoAnalysedResilienceTypeValues)
      .is("deleted_at", null);

    if (!adaptationRows?.length) return;

    const progressByAdaptationId = new Map<
      string,
      { total: number; completed: number; errors: number; pending: number }
    >();

    // each auto adaptation type is tracked individually but total count needs to be aggregated
    const updateAutoAdaptationsCount = () => {
      const currentSchema = this.schemas.find((s) => s.id === schema.id);
      if (!currentSchema || currentSchema.autoAdaptationStatus !== "pending") return;

      const combinedAutoAdaptations = { processedCount: 0, errorCount: 0, totalCount: 0, unprocessedCount: 0 };
      progressByAdaptationId.forEach((value) => {
        combinedAutoAdaptations.processedCount += value.completed;
        combinedAutoAdaptations.errorCount += value.errors;
        combinedAutoAdaptations.totalCount += value.total;
        combinedAutoAdaptations.unprocessedCount += value.pending;
      });

      this.updateCurrentSchema(schema, () => ({
        assets: completedAssets,
        adaptations: combinedAutoAdaptations,
      }));
    };

    await Promise.all(
      adaptationRows.map((adaptation) =>
        this.trackAdaptationTypeProgress({
          schema,
          controller,
          idx,
          adaptation,
          progressByAdaptationId,
          updateAutoAdaptationsCount,
        })
      )
    );
  };

  private trackAdaptationTypeProgress = async ({
    schema,
    controller,
    idx,
    adaptation,
    progressByAdaptationId,
    updateAutoAdaptationsCount,
  }: {
    schema: PortfolioExportJobSchema;
    controller: AbortController;
    idx: number;
    adaptation: { id: string; adaptation_type: string };
    progressByAdaptationId: Map<string, { total: number; completed: number; errors: number; pending: number }>;
    updateAutoAdaptationsCount: () => void;
  }) => {
    const { data: ids } = await store.dispatch(
      supabaseApi.endpoints.getAllPortfolioAdaptationAnalysisIds.initiate({
        portfolioId: schema.id,
        adaptationId: adaptation.id,
      })
    );
    if (!ids?.length) return;

    let retryStream = true;
    let isComplete = false;
    let lastValue: any = null;
    const allIds = await this.getDecryptedAnalysisIds(ids, schema);
    const encryptedIds = allIds.map((id) => id.decryptedId);

    while (retryStream) {
      const reader = await this.getReadableProgressStream({ encryptedIds, controller, schema });
      let isDone = false;

      while (!isDone) {
        await this.abortSchemasProgressIfNoSession();
        try {
          if (!reader) return;
          const { value } = await reader.read();

          if (value) {
            lastValue = value;
            if (value.pending === 0) {
              isDone = true;
              retryStream = false;
              isComplete = true;
            }

            progressByAdaptationId.set(adaptation.id, {
              total: value.total,
              completed: value.completed,
              errors: value.errors,
              pending: value.pending || 0,
            });
            updateAutoAdaptationsCount();

            logStream({ schema, value, idx });
          }
        } catch (err: unknown) {
          if (!reader) {
            isDone = true;
            retryStream = false;
            return;
          }

          if (err instanceof Error && err.message === "BodyStreamBuffer was aborted") {
            reader.releaseLock();
            isDone = true;
            retryStream = false;
            return;
          }

          console.error(`Error reading ${adaptation.adaptation_type} adaptation actions stream: `, err);
          isDone = true;
          reader.releaseLock();
          console.log(`Retrying ${adaptation.adaptation_type} adaptation actions stream in 2s`);
          await delay(2000);
        }
      }
    }

    if (isComplete && lastValue) {
      await store.dispatch(
        supabaseApi.endpoints.updateAdaptationProgressCounts.initiate({
          id: adaptation.id,
          total: lastValue.total,
          pending: lastValue.pending || 0,
          success: lastValue.completed,
          error: lastValue.errors,
        })
      );

      if (adaptation.adaptation_type === "enhanced_resilience") {
        await this.enqueueAdaptedSummary(schema);
      }
    }
  };

  private enqueueAdaptedSummary = async (schema: PortfolioExportJobSchema) => {
    const { data: adaptations } = await supabase
      .from("adaptations")
      .select("*")
      .eq("portfolio_id", schema.id)
      .eq("adaptation_type", "enhanced_resilience");
    const adaptedAdaptation = adaptations?.[0];
    if (!adaptedAdaptation) return;

    try {
      await enqueuePortfolioAdaptationSummary(
        schema.id,
        adaptedAdaptation.summary_id ?? "",
        schema.secret,
        adaptedAdaptation.stamps ?? [],
        adaptedAdaptation.id,
        schema.regionType
      );
    } catch (err) {
      console.error("Failed to enqueue adaptation summary:", err);
    }
  };

  /**
   * Abort if there's a mismatch between this.schemas and this.inProcessingSchemas.
   * This is usually due to cancellation by the user.
   */
  private abortCancelledSchemas = () => {
    try {
      this.inProcessingSchemas.map((processingSchema) => {
        if (!this.schemas.some((currentSchema) => currentSchema.id === processingSchema.id)) {
          processingSchema.controller.abort(PROGRESS_ABORTED_BY_USER);
          console.info(`Portfolio [${processingSchema.id}] aborted by the user`);
          this.inProcessingSchemas = this.inProcessingSchemas.filter((schema) => schema.id !== processingSchema.id);
        }
      });
    } catch (err) {
      console.error("Error while attempting to abort portfolio: ", err);
    }
  };

  private abortCompletedAdaptationSchemas = () => {
    try {
      this.inProcessingSchemas.forEach((processingSchema) => {
        const current = this.schemas.find((s) => s.id === processingSchema.id);
        if (
          current &&
          ["completed", "completed_with_errors"].includes(current.status) &&
          current.autoAdaptationStatus !== "pending"
        ) {
          processingSchema.controller.abort(PROGRESS_ABORTED_BY_USER);
          this.inProcessingSchemas = this.inProcessingSchemas.filter((s) => s.id !== processingSchema.id);
        }
      });
    } catch (err) {
      console.error("Error while attempting to abort completed adaptation tracking: ", err);
    }
  };

  /**
   * Abort requests to /progress if there's no session.
   */
  private abortSchemasProgressIfNoSession = async () => {
    const session = (await supabase.auth.getSession()).data.session;
    if (session) return;
    this.inProcessingSchemas.map((schema) => schema.controller.abort());
  };

  private updateAssetValues = async ({ value, schema }: { value: any; schema: PortfolioExportJobSchema }) => {
    const shouldUpdate = value.completed + value.errors === value.total;

    if (shouldUpdate) {
      const action = supabaseApi.endpoints.updatePortfolioProgressCounts.initiate({
        id: schema.id,
        pending: value.pending || 0,
        success: value.completed,
        error: value.errors,
        total: value.total,
      });

      return await store.dispatch(action);
    }
  };

  private getReadableProgressStream = async ({
    encryptedIds,
    controller,
    schema,
  }: {
    encryptedIds: string[];
    controller: AbortController;
    schema: PortfolioExportJobSchema;
  }): Promise<ReadableStreamDefaultReader<any> | undefined> => {
    return await streamProgress(encryptedIds, controller.signal, schema.regionType)
      .then((res) => {
        const stream: ReadableStream = res.stream;
        const transformStream = new PortfolioExportProgressTransformStream();
        const reader = stream.pipeThrough(transformStream).getReader();

        return reader;
      })
      .catch(async (err) => {
        if (err === PROGRESS_ABORTED_BY_USER) return;
        console.log("Retrying fetching progress in 5s: ", err);
        await delay(5000);
        return await this.getReadableProgressStream({ encryptedIds, controller, schema });
      });
  };

  /**
   * Iterates through schemas with a processing status and checks progress.
   */
  public progress = async (canRunAutoAdaptation: boolean) => {
    let errorCount = 0;
    this.abortCancelledSchemas();
    this.abortCompletedAdaptationSchemas();

    // 'processing' schemas yet to be included in this.inProcessingSchemas
    const newProcessingSchemas = this.schemas
      .filter((schema) => schema.status === "processing")
      .filter((schema) => !this.inProcessingSchemas.find(({ id }) => id === schema.id));

    await Promise.all(
      newProcessingSchemas.map(async (schema, idx) => {
        const action = supabaseApi.endpoints.getAllPortfolioAssetIds.initiate(schema.id);
        const controller = new AbortController();
        this.inProcessingSchemas.push({ id: schema.id, controller });

        return await store.dispatch(action).then(async ({ data: ids }) => {
          if (!ids) throw new Error("Failed to get asset IDs for portfolio.");

          let retryStream = true;
          const allIds = await this.getDecryptedAnalysisIds(ids, schema);
          const encryptedIds = allIds.map((id) => id.decryptedId);

          while (retryStream) {
            const reader = await this.getReadableProgressStream({ encryptedIds, controller, schema });
            let isDone = false;

            while (!isDone) {
              await this.abortSchemasProgressIfNoSession();
              try {
                if (!reader) return;
                const { value } = await reader.read();

                if (value) {
                  const status: ProcessStatus = "processing";
                  let isAssetAnalysisComplete = false;

                  if (errorCount !== value.errors) {
                    errorCount = value.errors;
                    await handleResultsError(allIds, encryptedIds, schema.secret, value.errors, schema.regionType);
                  }

                  if (value.pending === 0 && value.completed + value.errors === value.total) {
                    isDone = true;
                    isAssetAnalysisComplete = true;
                    retryStream = false;
                  }

                  this.updateCurrentSchema(schema, () => ({
                    assets: {
                      errorCount: value.errors,
                      processedCount: value.completed,
                      totalCount: value.total,
                      unprocessedCount: value.pending,
                    },
                    status,
                  }));

                  if (isAssetAnalysisComplete) {
                    if (canRunAutoAdaptation) {
                      await this.trackAdaptationPathwayActionsProgress({
                        schema,
                        controller,
                        idx,
                        completedAssets: {
                          errorCount: value.errors,
                          processedCount: value.completed,
                          totalCount: value.total,
                          unprocessedCount: value.pending,
                        },
                      });
                    }

                    await this.updateAssetValues({ schema, value });
                    store.dispatch(supabaseApi.util.invalidateTags([{ type: "Org", id: "ToolUsage" }]));

                    this.updateCurrentSchema(schema, (current) => ({
                      assets: {
                        errorCount: value.errors,
                        processedCount: value.completed,
                        totalCount: value.total,
                        unprocessedCount: value.pending,
                      },
                      status: "completed",
                      // a portfolio without an adaptation phase must stay null
                      autoAdaptationStatus: current.autoAdaptationStatus === null ? null : "completed",
                    }));

                    try {
                      if (schema.isLargeSite) {
                        await enqueueLargeSiteSummary(
                          schema.id,
                          schema.summaryId,
                          schema.secret,
                          schema.stamps,
                          schema.metadata
                        );
                      } else {
                        await enqueuePortfolioSummary(schema.id, schema.summaryId, schema.secret, schema.stamps);
                      }
                    } catch (err) {
                      console.error("Failed to enqueue summary generation:", err);
                    }

                    if (typeof Notification === "undefined" || Notification.permission !== "granted") {
                      return;
                    }

                    new Notification("Portfolio Analysis | Hub", {
                      body: `${schema.name} is complete`,
                      icon: "/favicon.ico",
                    });
                  }

                  logStream({ schema, value, idx });
                }
              } catch (err: unknown) {
                if (!reader) {
                  isDone = true;
                  retryStream = false;
                  return;
                }

                if (err instanceof Error && err.message === "BodyStreamBuffer was aborted") {
                  reader.releaseLock();
                  isDone = true;
                  retryStream = false;
                  return;
                }

                console.error("Error reading stream: ", err);
                isDone = true;
                reader.releaseLock();
                console.log("Retrying reading stream in 2s");
                await delay(2000);
              }
            }
          }
        });
      })
    );

    // on reload, check if adaptations are in progress (for when assets have complete processing)
    if (canRunAutoAdaptation) {
      const adaptationOnlySchemas = this.schemas
        .filter((schema) => schema.status === "completed" || schema.status === "completed_with_errors")
        .filter((schema) => schema.autoAdaptationStatus === "pending")
        .filter((schema) => !this.inProcessingSchemas.find(({ id }) => id === schema.id));

      await Promise.all(
        adaptationOnlySchemas.map(async (schema, idx) => {
          const controller = new AbortController();
          this.inProcessingSchemas.push({ id: schema.id, controller });

          await this.trackAdaptationPathwayActionsProgress({
            schema,
            controller,
            idx,
            completedAssets: schema.assets,
          });
        })
      );
    }
  };
}

const handleResultsError = async (
  allIds: DecryptedIdMaps,
  uuids: string[],
  secret: SecretSalt,
  errors: any,
  regionType?: PortfolioExportJobSchema["regionType"]
) => {
  if (!errors) return;
  console.log("[handleResultsError]");
  const res = await streamResultsErrorsOnly(uuids, regionType).catch((err) => {
    console.error("Unknown error while fetching errors in results: ", err);
    throw new Error("Unknown error while fetching errors in results.");
  });

  // match up with supabase id

  const stream: ReadableStream = res.stream;
  const transformStream = new PortfolioExportResultsTransformStream();
  const reader = stream.pipeThrough(transformStream).getReader();

  let isDone = false;

  while (!isDone) {
    const { value, done } = await reader.read();

    if (value) {
      const current = allIds.find((id) => value.id === id.decryptedId);
      console.log("[handleResultsError][current]", current);

      if (!current) return;

      const action = supabaseApi.endpoints.updatePortfolioAssetsWithErrors.initiate({
        errors: await encryptData(secret, JSON.stringify(value)),
        id: current.supabaseId,
      });
      const result = await store.dispatch(action);
      if ("error" in result) {
        console.error("[handleResultsError][sb.updatePortfolioAssetsWithErrors]", result.error);
      }
    }
    if (done) {
      isDone = true;
    }
  }
};

const logStream = ({ schema, value, idx }: any) => {
  const colors = ["#fb923c", "#2987D9", "#84cc16", "#97CE81", "#DAD356"];

  console.log(
    `%c [PortfolioExport]: ${idx + 1} `,
    `background: ${colors[idx]}; color: black`,
    `${schema.id.slice(0, 7)}: ${JSON.stringify(value)}`
  );
};
